import type { IconName } from './icons'

/**
 * The sections of the console, as data.
 *
 * They were twelve flat links in the masthead until this file existed, and the arrangement broke the
 * phone layout twice as phases added to it (D-168, D-169). Grouping was the right fix for that
 * symptom. What it left was groups named for engineering categories - Monitor, Authoring, Fleet,
 * Administration - so an operator had to know which category held the thing they wanted, and several
 * entries were not destinations at all but views of one subject: dictionaries are policy inputs,
 * events are alerts unfolded, deployment and trust are facets of a device.
 *
 * So groups now describe **where a link is rendered** rather than what kind of thing it is.
 *
 * 'primary' is the rail on a desktop and the bottom tab bar on a phone: five destinations an operator
 * works in all day, every one reachable without opening anything. 'account' is the menu under the
 * identity in the top bar: configuration, visited at setup and seldom after.
 *
 * Five, and the number is a constraint rather than a coincidence. It is what a bottom tab bar can
 * carry with a readable label under each; a sixth would either drop the labels or need an overflow,
 * and both are worse than one extra click on a screen nobody opens daily. `navigation.test.ts`
 * asserts it, so a sixth fails there rather than in a screenshot (D-323).
 *
 * The trade being made deliberately: Administration is one click further away than it was. Five
 * destinations that are all reachable without a menu, against six where every one costs a menu.
 *
 * As data rather than markup because several components render it — the rail, the phone tabs and the
 * account menu — and a second hand-written copy is how they stop agreeing about what exists.
 */
export interface NavItem {
  to: string
  label: string
  /**
   * The roles that may reach this item. Absent means everyone who is signed in.
   *
   * This hides what somebody cannot use; it does not protect it. The API refuses the request either
   * way, and it is the only thing that can - a browser is not a place to enforce authority. What this
   * buys is that an analyst is never shown a control whose only possible answer is 403, which is a
   * way of wasting their time.
   */
  roles?: readonly string[]
}

/** Where a group is rendered. Not what kind of thing it is - that is what this replaced. */
export type NavPlacement = 'primary' | 'account'

export interface NavGroup {
  label: string
  placement: NavPlacement
  items: NavItem[]
  roles?: readonly string[]
  /** Primary groups only: the rail and the tab bar are icon-first. Typed, so a typo fails `tsc`. */
  icon?: IconName
}

/**
 * The four roles that belong to an organization rather than to the deployment.
 *
 * The screens gated on these are about a fleet, and the platform organization has no fleet - no
 * devices, no policies, no events. Showing them to a platform administrator would offer a set of
 * screens that can only ever be empty, which reads as a broken deployment rather than as a category
 * error. They become visible the moment that administrator opens a customer organization, because an
 * assumed session carries one of exactly these roles.
 */
const CUSTOMER_ROLES = ['owner', 'administrator', 'analyst', 'viewer'] as const

/**
 * Not a role anybody holds: what the navigation adds for a platform administrator inside a customer
 * organization, so the few things support is for can be reached from where support is being given.
 *
 * Never sent to the server and never in a token. A support session's own roles are the customer's
 * and stop below owner (ADR-S-043); what this unlocks on screen is backed by the platform session
 * parked behind it, whose authority already covers those acts (D-533).
 */
export const SUPPORT_ROLE = 'platform-support'

/** The roles the navigation is drawn for: the session's own, plus `SUPPORT_ROLE` inside a support session. */
export function navigationRoles(identity: { roles: readonly string[]; assumedByName?: string | null }): readonly string[] {
  return identity.assumedByName ? [...identity.roles, SUPPORT_ROLE] : identity.roles
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Board',
    placement: 'primary',
    icon: 'gauge',
    roles: CUSTOMER_ROLES,
    items: [{ to: '/board', label: 'Board' }]
  },
  {
    // Alerts, events and explore, folded into one. An alert was already a fold of occurrences; the
    // raw feed is that fold opened up, and the query bar is Explore made always-present rather than
    // a screen somebody has to remember exists.
    label: 'Cases',
    placement: 'primary',
    icon: 'warning-diamond',
    roles: CUSTOMER_ROLES,
    items: [{ to: '/cases', label: 'Cases' }]
  },
  {
    // New. Exposure by person rather than by rule, because the question a director asks is who is
    // sending company data out, and nothing here answered it.
    label: 'People',
    placement: 'primary',
    icon: 'users-three',
    roles: CUSTOMER_ROLES,
    items: [{ to: '/people', label: 'People' }]
  },
  {
    label: 'Policies',
    placement: 'primary',
    icon: 'scroll',
    roles: CUSTOMER_ROLES,
    items: [{ to: '/policies', label: 'Policies' }]
  },
  {
    label: 'Devices',
    placement: 'primary',
    icon: 'desktop-tower',
    roles: CUSTOMER_ROLES,
    items: [{ to: '/devices', label: 'Devices' }]
  },
  {
    label: 'Administration',
    placement: 'account',
    items: [
      // An owner manages everything here. Support reaches it too, for the acts that get somebody
      // back in - a password, a lockout, a disabled account - and nothing else (D-533).
      { to: '/accounts', label: 'Accounts', roles: ['owner', SUPPORT_ROLE] },
      // The organization's own identity - its color, its mark, the host its people sign in on.
      // Administrators as well as owners, because it is the same class of thing as who may sign in
      // and an owner who has delegated that has delegated this. An analyst cannot reach it: the API
      // gates it at ManageAccounts, so offering it to one would be offering a guaranteed 403.
      { to: '/branding', label: 'Branding', roles: ['owner', 'administrator'] },
      { to: '/export', label: 'Export', roles: CUSTOMER_ROLES },
      { to: '/system', label: 'System', roles: CUSTOMER_ROLES },
      // Appearance belongs to the person rather than to the organization, so everybody who can sign
      // in has it - a platform administrator included.
      { to: '/settings', label: 'Settings' }
    ]
  },
  {
    // A group of its own rather than another Administration entry. Running the deployment and
    // running an organization on it are different jobs held by different people, and folding them
    // together would make "Administration" mean two things depending on who was reading it.
    //
    // It is invisible to everybody except a platform administrator, which is every operator of every
    // customer organization - so for almost all users this group does not exist.
    label: 'Platform',
    placement: 'account',
    roles: ['platform-admin'],
    items: [
      { to: '/platform/tenants', label: 'Organizations', roles: ['platform-admin'] },
      { to: '/platform/administrators', label: 'Administrators', roles: ['platform-admin'] }
    ]
  }
]

/** Every route the navigation reaches, flattened. Used to decide which group is the current one. */
export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

/** Whether a set of roles satisfies a requirement. Absent requirement means everybody. */
export function permits(required: readonly string[] | undefined, roles: readonly string[]): boolean {
  if (!required) return true
  return required.some((role) => roles.includes(role))
}

/**
 * The navigation as one particular operator sees it.
 *
 * Derived from the same data both renderers already use, rather than being a second filtered copy.
 * The desktop bar and the phone drawer must not be able to disagree about what exists, and a
 * hand-written second list is exactly how they would.
 *
 * A group whose items are all hidden is dropped entirely, so nobody is shown an empty heading.
 */
export function navGroupsFor(roles: readonly string[]): readonly NavGroup[] {
  return NAV_GROUPS.filter((group) => permits(group.roles, roles))
    .map((group) => ({ ...group, items: group.items.filter((item) => permits(item.roles, roles)) }))
    .filter((group) => group.items.length > 0)
}

/**
 * The primary destinations one operator sees: the rail on a desktop, the tab bar on a phone.
 *
 * A filter over `navGroupsFor` rather than its own list, and here rather than in the components that
 * render it, for exactly the reason the file's own comment gives for `navGroupsFor` existing at all.
 * Three things draw this now instead of two, and three hand-written filters is three chances for them
 * to disagree about what exists.
 */
export function primaryNavFor(roles: readonly string[]): readonly NavGroup[] {
  return navGroupsFor(roles).filter((group) => group.placement === 'primary')
}

/** The configuration under the identity in the top bar: Administration, and Platform for those who have it. */
export function accountNavFor(roles: readonly string[]): readonly NavGroup[] {
  return navGroupsFor(roles).filter((group) => group.placement === 'account')
}

/**
 * The group holding the route being viewed, so the bar can mark it.
 *
 * Matched on the leading segment because most sections have detail routes beneath them — an alert
 * being read is still Alerts — and an exact comparison would leave the bar showing nothing selected
 * on precisely the screens somebody navigates to most deliberately.
 */
export function groupFor(pathname: string): NavGroup | undefined {
  // The longest item this path sits under, rather than its first segment. Matching on the leading
  // segment alone was enough while every section was one segment deep, and silently stopped marking
  // anything once a section was not: "/platform/tenants" reduces to "/platform", which is no item's
  // route, so the bar showed nothing selected on precisely the screen somebody had just chosen.
  const candidates = NAV_ITEMS.filter(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`)
  ).sort((left, right) => right.to.length - left.to.length)

  const best = candidates[0]
  return best ? NAV_GROUPS.find((group) => group.items.includes(best)) : undefined
}

/**
 * Whether these roles can reach this path, for deciding where to put somebody after they sign in.
 *
 * Matched on the longest item the path sits under, the way `groupFor` does, because most sections
 * have detail routes beneath them and a session that ended on `/cases/some-alert` should come back
 * to it. A path under no known item answers false: an unrecognised route is not somewhere to send
 * anybody, and `homeRouteFor` is a better answer than a redirect loop.
 *
 * This decides a destination; it does not grant anything. The API refuses what the roles cannot do
 * either way, which is the only place that can be decided (D-294).
 */
export function canReach(pathname: string, roles: readonly string[]): boolean {
  if (!pathname.startsWith('/')) return false

  // The query goes with the route it belongs to, and is not part of matching one.
  const path = pathname.split('?')[0] ?? pathname

  // Never somewhere to return to. Signing in and landing back on the sign-in page reads as the sign
  // in having failed, and the change-password screen is a takeover nobody navigates to.
  if (path === '/sign-in') return false

  const reachable = navGroupsFor(roles).flatMap((group) => group.items)
  const best = reachable
    .filter((item) => path === item.to || path.startsWith(`${item.to}/`))
    .sort((left, right) => right.to.length - left.to.length)[0]

  return best !== undefined
}

/**
 * Where somebody lands when they arrive with no route of their own.
 *
 * The first thing their roles can actually reach, rather than a fixed route. A platform
 * administrator sent to Policies would open a screen about a fleet, on the one organization that has
 * none - which reads as a broken deployment rather than as a screen that was never meant for them.
 */
export function homeRouteFor(roles: readonly string[]): string {
  const visible = navGroupsFor(roles)

  // A group that names roles was written for somebody in particular, so it wins over one that is
  // open to everybody. Without this a platform administrator lands on Settings, because the group
  // holding the only other thing they can reach happens to be declared later in the list - which
  // makes where they arrive depend on the order of a file rather than on who they are.
  const theirs = visible.find((group) => group.roles !== undefined)
  return (theirs ?? visible[0])?.items[0]?.to ?? '/settings'
}
